import { useState } from "react";

import { Form } from "react-final-form";
import { useDispatch } from "react-redux";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";

import { FormattedMessage } from "@mnemonic/intl";

import { TextField } from "../../Components/MuiFinalForm";
import { PasswordChecker } from "../../Components/PasswordChecker";
import { SubmitButton } from "../../Components/SubmitButton";
import { useFetchBreachStatusMutation, useUpdatePasswordMutation } from "../../Redux/Service/authenticateApi";
import { globalSlice } from "../../Redux/globalSlice";
import { commonMessages } from "../../commonMessages";
import { validatePassword } from "../Login/utils/validate";
import { type IEnrollUsernamePassword } from "./useVerifyUsernamePassword";
import { mapErrors } from "./utils";

type TForm = {
  newPassword: string;
};

export const StepDefinePassword = ({ enrollFlowState }: IEnrollUsernamePassword) => {
  const [showPassword, setShowPassword] = useState(false);
  const [definePassword] = useUpdatePasswordMutation();
  const [checkPassword] = useFetchBreachStatusMutation();
  const sessionKey = enrollFlowState?.setPasswordAuthorization?.sessionKey;

  const dispatch = useDispatch();
  function redirect() {
    // If user has excess authorization tokens, redirect them to PostEnrollment dialog
    if (enrollFlowState?.authorizations?.length > 0) dispatch(globalSlice.actions.setNextUrl(`/spa/user/profile`));
    dispatch(globalSlice.actions.setLoginSuccess(true));
  }

  const passwordAvailable = async (value: string) => {
    const passwordError = validatePassword(value);
    if (passwordError) return passwordError;

    const results = await checkPassword({ criteria: { password: value }, metaOpts: { sessionKey } }).unwrap();

    if (results?.data?.breachCount !== 0) {
      return (
        <FormattedMessage
          id="validation.breached.password"
          defaultMessage="This password has been breached. Please choose a different one."
        />
      );
    }
    return undefined;
  };

  return (
    <Box>
      <Typography variant="body1">
        <FormattedMessage
          id="login.verify.definePassword.info"
          defaultMessage="Your user account has been verified. Please set a password to log in to your account"
        />
      </Typography>
      <Form<TForm>
        onSubmit={async ({ newPassword }) => {
          try {
            await definePassword({
              criteria: { newPassword, authorization: enrollFlowState?.setPasswordAuthorization?.authorization },
              metaOpts: { sessionKey },
            }).unwrap();
            return redirect();
          } catch (e) {
            return mapErrors(e, ["password"]);
          }
        }}
        render={({ handleSubmit, submitError, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Box sx={({ spacing }) => ({ mt: spacing(3) })}>
                <TextField
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            aria-label="toggle password visibility"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  fullWidth
                  label={<FormattedMessage {...commonMessages.SetPassword} />}
                  name="newPassword"
                  type={showPassword ? "text" : "password"}
                  validate={passwordAvailable}
                />
                <PasswordChecker newPassword={values?.newPassword} sessionKey={sessionKey} />
              </Box>
              {submitError && (
                <Alert variant="filled" severity="error" sx={{ mt: 2 }}>
                  {submitError}
                </Alert>
              )}
              <SubmitButton>
                <FormattedMessage {...commonMessages.SetPassword} />
              </SubmitButton>
            </form>
          );
        }}
      />
    </Box>
  );
};
